import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CatalogDialog } from '@/components/catalog/CatalogDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DataTable, type Column } from '@/components/DataTable';
import type { CreateCatalogRequest, UpdateCatalogRequest } from '@/types/catalog';
import { api } from '@/lib/api';
import type { Catalog, CatalogStack } from '@/types/catalog';
import type { Endpoint } from '@/types/endpoint';
import { toast } from 'sonner';

function truncateCompose(content: string, maxLength: number = 50): string {
  if (!content) return '-';
  const firstLine = content.split('\n')[0] || content;
  if (firstLine.length <= maxLength) return firstLine;
  return firstLine.substring(0, maxLength) + '...';
}

export function CatalogStacksPage() {
  const { catalogId } = useParams<{ catalogId: string }>();
  // Keyed by catalog: switching catalogs remounts the view and starts from the loading state.
  return <CatalogStacksView key={catalogId} catalogId={catalogId} />;
}

function CatalogStacksView({ catalogId }: { catalogId: string | undefined }) {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [stacks, setStacks] = useState<CatalogStack[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<CatalogStack | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const catalogData = await api.catalog.get(Number(catalogId));
        const stacksData = await api.catalog.stacks.list(Number(catalogId), { show_deleted: true });
        // Best-effort: endpoint names are only used to label locked endpoints.
        let eps: Endpoint[] = [];
        try {
          eps = await api.endpoints.listAll();
        } catch {
          eps = [];
        }
        if (cancelled) return;
        setCatalog(catalogData);
        setStacks(stacksData);
        setEndpoints(eps);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : 'Failed to load data');
        navigate('/catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [catalogId, navigate, reloadKey]);

  const visibleStacks = useMemo(
    () => (showDeleted ? stacks : stacks.filter((s) => s.is_active)),
    [stacks, showDeleted],
  );

  // Resolve locked endpoint IDs to their names (best-effort).
  const lockedEndpointNames = useMemo(() => {
    if (!catalog?.locked_endpoints || catalog.locked_endpoints.length === 0) return [];
    return catalog.locked_endpoints.map(id => {
      const ep = endpoints.find(e => e.id === id);
      return ep ? ep.name : `#${id}`;
    });
  }, [catalog, endpoints]);

  const runDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const result = await api.catalog.stacks.delete(Number(catalogId), deleting.id);
      setDeleteOpen(false);
      if (result.warning) {
        toast.warning(result.warning);
      } else {
        toast.success('Stack deleted');
      }
      reload();
    } catch (err) {
      setDeleteError(err instanceof Error && err.message ? err.message : 'Failed to delete stack');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleRestore = async (stackId: number) => {
    try {
      await api.catalog.stacks.restore(Number(catalogId), stackId);
      toast.success('Stack restored');
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore stack');
    }
  };

  const handleEditCatalog = async (data: CreateCatalogRequest | UpdateCatalogRequest) => {
    if (!catalog) return;
    await api.catalog.update(catalog.id, data as UpdateCatalogRequest);
    toast.success('Catalog updated');
    reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Catalog not found
      </div>
    );
  }

  const stackColumns: Column<CatalogStack>[] = [
    { key: 'name', header: 'Name', cell: (stack) => <span className="font-medium [overflow-wrap:anywhere]">{stack.name}</span>, sortValue: (stack) => stack.name },
    {
      key: 'compose',
      header: 'Compose',
      cell: (stack) => <span className="font-mono text-sm">{truncateCompose(stack.compose_content)}</span>,
      className: 'max-w-xs',
    },
    { key: 'cpu', header: 'CPU', cell: (stack) => stack.cpu_limit, sortValue: (stack) => stack.cpu_limit },
    { key: 'memory', header: 'Memory', cell: (stack) => `${stack.memory_mb} MB`, sortValue: (stack) => stack.memory_mb },
    { key: 'storage', header: 'Storage', cell: (stack) => `${stack.storage_gb} GB`, sortValue: (stack) => stack.storage_gb },
    {
      key: 'env',
      header: 'Env vars',
      cell: (stack) => <Badge variant="outline">{Object.keys(stack.env_vars || {}).length} vars</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (stack) => (
        <Badge variant={stack.is_active ? 'default' : 'destructive'}>{stack.is_active ? 'Active' : 'Deleted'}</Badge>
      ),
      sortValue: (stack) => (stack.is_active ? 'active' : 'deleted'),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      cell: (stack) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" aria-label={`Edit ${stack.name}`} onClick={() => navigate(`/catalog/${catalogId}/stacks/${stack.id}`)}>
            Edit
          </Button>
          {stack.is_active ? (
            <Button
              size="sm"
              variant="destructive"
              aria-label={`Delete ${stack.name}`}
              onClick={() => {
                setDeleteError(null);
                setDeleting(stack);
                setDeleteOpen(true);
              }}
            >
              Delete
            </Button>
          ) : (
            <Button size="sm" variant="outline" aria-label={`Restore ${stack.name}`} onClick={() => handleRestore(stack.id)}>
              Restore
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm">
        <Link to="/catalog" className="text-muted-foreground hover:text-foreground">
          Catalogs
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="font-medium">{catalog.name}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-medium">Stacks</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/catalog" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{catalog.name}</h1>
            <p className="text-muted-foreground">{catalog.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Catalog
          </Button>
          <Button onClick={() => navigate(`/catalog/${catalogId}/stacks/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Stack
          </Button>
        </div>
      </div>

      {/* Catalog Info */}
      <Card>
        <CardHeader>
          <CardTitle>Catalog Details</CardTitle>
          <CardDescription>Configuration for {catalog.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3 text-sm">
            <div>
              <div className="text-muted-foreground">Status</div>
              <div>
                <Badge variant={catalog.is_active ? 'default' : 'destructive'}>
                  {catalog.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Total Stacks</div>
              <div className="font-medium">{catalog.stacks_count}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Expose Port</div>
              <div className="font-medium">
                {catalog.expose_port ? catalog.expose_port : '— (no public routing)'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Target Mode</div>
              <div className="font-medium">
                {catalog.target_mode === 'locked'
                  ? 'Locked'
                  : catalog.target_mode === 'auto'
                  ? 'Auto'
                  : 'Auto (default)'}
              </div>
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="text-muted-foreground">Created At</div>
              <div className="font-medium">
                {catalog.created_at ? new Date(catalog.created_at).toLocaleString() : '—'}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Additional Exposed Ports</div>
              {catalog.additional_ports && catalog.additional_ports.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {catalog.additional_ports.map((p, i) => (
                    <Badge key={i} variant="outline" className="font-mono">
                      {p.prefix ? `${p.prefix}:` : ''}{p.port}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">None</div>
              )}
            </div>

            {catalog.target_mode === 'locked' && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  Locked Endpoints ({lockedEndpointNames.length})
                </div>
                {lockedEndpointNames.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {lockedEndpointNames.map(name => (
                      <Badge key={name} variant="secondary">{name}</Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-destructive">
                    No endpoints selected. Locked mode requires at least one endpoint.
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Catalog stacks</CardTitle>
          <CardDescription>Docker Compose templates for {catalog.name} deployments</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<CatalogStack>
            columns={stackColumns}
            rows={visibleStacks}
            getRowKey={(stack) => stack.id}
            searchText={(stack) => stack.name}
            searchPlaceholder="Search stacks…"
            toolbar={
              <div className="flex items-center gap-2">
                <input
                  id="showDeleted"
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  className="size-4 accent-primary"
                />
                <label htmlFor="showDeleted" className="text-sm">
                  Show deleted
                </label>
              </div>
            }
            empty={{
              title: showDeleted ? 'No stacks match' : 'No stacks yet',
              description: 'Create a stack to give users a template they can deploy.',
            }}
            onRowClick={(stack) => navigate(`/catalog/${catalogId}/stacks/${stack.id}`)}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusy) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete stack?"
        description={deleting ? `Delete '${deleting.name}'? It will be hidden from users and can be restored from Show deleted.` : undefined}
        confirmLabel={deleteBusy ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />

      <CatalogDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        catalog={catalog}
        onSubmit={handleEditCatalog}
      />
    </div>
  );
}